
import type React from "react"

import { useState, useRef } from "react"
import { useTranslation } from "react-i18next"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { useToast } from "@/hooks/use-toast"
import { apiRequest } from "@/lib/api-client"

export function PasswordForm() {
  const { t } = useTranslation()
  const { toast } = useToast()
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const formRef = useRef<HTMLFormElement>(null)

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault()
    setIsLoading(true)
    setError(null)

    const form = e.currentTarget
    const formData = new FormData(form)
    const oldPassword = formData.get("currentPassword") as string
    const newPassword = formData.get("newPassword") as string
    const confirmPassword = formData.get("confirmPassword") as string

    // Validate passwords match
    if (newPassword !== confirmPassword) {
      setError(t("profile.passwordsDoNotMatch"))
      setIsLoading(false)
      return
    }

    try {
      // Make API call to update password
      await apiRequest({
        method: "POST",
        url: "/auth/update-password",
        data: {
          oldPassword,
          newPassword,
        },
      })

      toast({
        title: t("common.success"),
        description: t("profile.passwordUpdated"),
      })

      // Reset form - safely access the form
      if (formRef.current) {
        formRef.current.reset()
      }
    } catch (err) {
      console.error("Password update error:", err)

      // Show error in form
      if (err instanceof Error) {
        setError(err.message)
      } else {
        setError(t("profile.passwordUpdateFailed"))
      }

      toast({
        title: t("common.error"),
        description: err instanceof Error ? err.message : t("profile.passwordUpdateFailed"),
        variant: "destructive",
      })
    } finally {
      setIsLoading(false)
    }
  }

  return (
    <form ref={formRef} onSubmit={handleSubmit} className="space-y-4">
      {error && <div className="p-3 rounded-md bg-destructive/10 text-destructive text-sm">{error}</div>}
      <div className="space-y-4">
        <div className="space-y-2">
          <Label htmlFor="currentPassword">{t("profile.currentPassword")}</Label>
          <Input id="currentPassword" name="currentPassword" type="password" required />
        </div>
        <div className="space-y-2">
          <Label htmlFor="newPassword">{t("profile.newPassword")}</Label>
          <Input id="newPassword" name="newPassword" type="password" required />
        </div>
        <div className="space-y-2">
          <Label htmlFor="confirmPassword">{t("profile.confirmPassword")}</Label>
          <Input id="confirmPassword" name="confirmPassword" type="password" required />
        </div>
      </div>
      <div className="flex justify-end">
        <Button type="submit" disabled={isLoading}>
          {isLoading ? t("common.loading") : t("profile.updatePassword")}
        </Button>
      </div>
    </form>
  )
}
